import { makeChoices } from "../../distractors";
import { signed, text } from "../../format";
import type { QuestionDraft, Rng, TopicDef } from "../../types";
import { both, numeric, q } from "../helpers";

function tier1(rng: Rng): QuestionDraft {
  const a = rng.int(2, 9);
  const b = rng.int(-10, 10);
  const x = rng.int(1, 10);
  const value = a * x + b;
  const expr = b >= 0 ? `${a}x + ${b}` : `${a}x − ${-b}`;
  const choice4 = makeChoices(rng, value, [a + x + b, a * (x + b), value - a, value + a], (r) =>
    r.int(-20, 100),
  );
  return q(
    both(`Oblicz wartość wyrażenia ${expr} dla x = ${x}.`, `Find the value of ${expr} for x = ${x}.`),
    numeric(value),
    [
      `Podstawiamy x = ${x}: ${a} · ${x} ${b >= 0 ? "+" : "−"} ${Math.abs(b)}.`,
      `${a * x} ${b >= 0 ? "+" : "−"} ${Math.abs(b)} = ${value}.`,
    ],
    [
      `Substitute x = ${x}: ${a} × ${x} ${b >= 0 ? "+" : "−"} ${Math.abs(b)}.`,
      `${a * x} ${b >= 0 ? "+" : "−"} ${Math.abs(b)} = ${value}.`,
    ],
    { choice4 },
  );
}

function tier2(rng: Rng): QuestionDraft {
  const a = rng.int(2, 8);
  const b = rng.int(2, 8);
  const x = rng.int(1, 9);
  const y = rng.int(1, 9);
  const value = a * x - b * y;
  const choice4 = makeChoices(rng, value, [a * x + b * y, -value, a * y - b * x, value + 1], (r) =>
    r.int(-60, 80),
  );
  return q(
    both(
      `Oblicz wartość wyrażenia ${a}x − ${b}y dla x = ${x} i y = ${y}.`,
      `Find the value of ${a}x − ${b}y for x = ${x} and y = ${y}.`,
    ),
    numeric(value),
    [`${a} · ${x} = ${a * x}, ${b} · ${y} = ${b * y}.`, `${a * x} − ${b * y} = ${value}.`],
    [`${a} × ${x} = ${a * x}, ${b} × ${y} = ${b * y}.`, `${a * x} − ${b * y} = ${value}.`],
    { choice4 },
  );
}

function tier3(rng: Rng): QuestionDraft {
  const a = rng.int(1, 6);
  const b = rng.int(1, 15);
  const x = rng.int(-6, 6) || 2;
  const value = x * x + a * x - b;
  const expr = `x² + ${a}x − ${b}`;
  const wrongSquare = -(x * x) + a * x - b;
  const choice4 = makeChoices(rng, value, [wrongSquare, 2 * x + a * x - b, value + 2 * Math.abs(x)], (r) =>
    r.int(-40, 80),
  );
  return q(
    both(`Oblicz wartość wyrażenia ${expr} dla x = ${x}.`, `Find the value of ${expr} for x = ${x}.`),
    numeric(value),
    [
      `x² = ${signed(x)} · ${signed(x)} = ${x * x}.`,
      `${a}x = ${a} · ${signed(x)} = ${a * x}.`,
      `${x * x} ${a * x >= 0 ? "+" : "−"} ${Math.abs(a * x)} − ${b} = ${value}.`,
    ],
    [
      `x² = ${signed(x)} × ${signed(x)} = ${x * x}.`,
      `${a}x = ${a} × ${signed(x)} = ${a * x}.`,
      `${x * x} ${a * x >= 0 ? "+" : "−"} ${Math.abs(a * x)} − ${b} = ${value}.`,
    ],
    { choice4 },
  );
}

export const expressionsEval: TopicDef = {
  id: "sp6.expressions_eval",
  level: "sp6",
  name: text("Wartość wyrażenia algebraicznego", "Evaluating expressions"),
  modes: ["numeric", "choice4"],
  generate: (rng, tier) => (tier === 1 ? tier1(rng) : tier === 2 ? tier2(rng) : tier3(rng)),
};
